import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSeoMeta, useSocialMeta } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { AppShell } from '@/components/AppShell';
import { Progress } from '@/components/ui/progress';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ProfileStep } from '@/components/assessment/ProfileStep';
import { QuestionStep } from '@/components/assessment/QuestionStep';
import { ResultsStep } from '@/components/assessment/ResultsStep';
import { expandThemeScoresToGroups } from '@/lib/calculationEngine';
import logger from '@/lib/logger';

const API = '/api';

export default function AssessmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { lang, localePath } = useLocale();
  useSeoMeta(t('seo.pages.assessmentTitle'), t('seo.pages.assessmentDescription'));
  useSocialMeta({
    title: t('seo.pages.assessmentTitle'),
    description: t('seo.pages.assessmentDescription'),
    url: `https://ismscalculator.com/${lang}/assessment`,
  });

  // Read any state saved before an auth redirect exactly once (lazy initializer),
  // then hydrate the form from it. Avoids both ref-in-render and setState-in-effect.
  const [savedState] = useState(() => {
    try {
      const raw = sessionStorage.getItem('assessment_state');
      return raw ? { ...JSON.parse(raw), _hadSaved: true } : {};
    } catch {
      return {};
    }
  });

  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(savedState.step || 0);
  const [industry, setIndustry] = useState(savedState.industry || '');
  const [companySize, setCompanySize] = useState(savedState.companySize || '');
  const [selections, setSelections] = useState(savedState.selections || {});
  const [contexts, setContexts] = useState(savedState.contexts || {});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [pendingSubmit] = useState(!!savedState._hadSaved);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/assessment/questions`).then(r => {
      setQuestions(r.data.questions);
    }).catch(err => {
      logger.warn('[AssessmentPage] Failed to load questions:', err?.message || err);
    });
    // Saved state (if any) was hydrated via initializers; clear it so a manual
    // refresh starts a fresh assessment.
    sessionStorage.removeItem('assessment_state');
  }, []);

  const totalSteps = questions.length + 1; // profile + questions
  const isProfileStep = step === 0;
  const isQuestionStep = step >= 1 && step <= questions.length;
  const isResultStep = result !== null;
  const currentQ = isQuestionStep ? questions[step - 1] : null;
  const progress = isResultStep ? 100 : Math.round((step / totalSteps) * 100);

  let canProceed = false;
  if (isProfileStep) canProceed = !!(industry && companySize);
  else if (isQuestionStep) canProceed = !!selections[currentQ?.id];

  // Build combined answers before submit (selection::context format)
  const buildAnswers = useCallback(() => {
    const combined = {};
    questions.forEach(q => {
      const sel = selections[q.id] || '';
      const ctx = (contexts[q.id] || '').trim();
      combined[q.id] = ctx ? `${sel}::${ctx}` : sel;
    });
    return combined;
  }, [questions, selections, contexts]);

  const handleSubmitDirect = useCallback(async () => {
    setLoading(true);
    setStep(totalSteps + 1);
    try {
      const combined = buildAnswers();
      const res = await axios.post(`${API}/assessment`, { answers: combined, industry, company_size: companySize, lang }, {
        withCredentials: true,
      });
      setResult(res.data.result);
      setAssessmentId(res.data.assessment_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('assessment.assessmentFailed'));
      setStep(totalSteps);
    } finally {
      setLoading(false);
    }
  }, [buildAnswers, industry, companySize, totalSteps, t, lang]);

  // Auto-submit after auth redirect when state is restored
  useEffect(() => {
    if (pendingSubmit && !autoSubmittedRef.current && user && questions.length > 0 && Object.keys(selections).length > 0) {
      autoSubmittedRef.current = true;
      handleSubmitDirect();
    }
  }, [pendingSubmit, user, questions, selections, handleSubmitDirect]);

  const handleSubmit = async () => {
    if (!user) {
      // Save state before redirecting to auth
      sessionStorage.setItem('assessment_state', JSON.stringify({
        industry, companySize, selections, contexts, step,
      }));
      toast.info(t('assessment.signInForResults'));
      navigate(localePath('/auth'), { state: { from: localePath('/assessment') } });
      return;
    }
    await handleSubmitDirect();
  };

  const handleEmail = async () => {
    setEmailing(true);
    try {
      await axios.post(`${API}/assessment/email`, { assessment_id: assessmentId, lang: i18n.language }, {
        withCredentials: true,
      });
      toast.success(t('assessment.reportSent'));
    } catch (err) {
      logger.warn('[AssessmentPage] Email send failed:', err?.message || err);
      toast.error(t('assessment.emailFailed'));
    } finally {
      setEmailing(false);
    }
  };

  const handleUseInCalculator = () => {
    // The AI returns 4 ISO 27001:2022 theme scores; expand them across the
    // advanced assessment's control groups so the calculator pre-fills.
    const ms = result?.maturity_scores;
    const expanded = ms ? expandThemeScoresToGroups(ms) : null;
    navigate(localePath('/calculator'), expanded ? { state: { maturityScores: expanded } } : undefined);
  };

  return (
    <AppShell>

      <div className="focused-form assessment-workspace">
        {/* Header (hidden once results are displayed) */}
        {!isResultStep && (
          <>
            <div className="mb-8">
              <div className="data-eyebrow inline-flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4" /> {t('assessment.badge')}
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="assessment-heading">
                {t('assessment.title')}
              </h1>
              <p className="mt-2 text-sm text-[#66707A]">
                {t('assessment.subtitle')}
              </p>
              <div
                className="mt-6 border-l-2 border-[#2867B2] bg-white px-4 py-3"
                data-testid="ai-disclosure"
              >
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#2867B2]"
                     style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {t('trust.aiAssisted')}
                </div>
                <p className="mt-1 text-xs text-[#66707A]">{t('trust.aiDisclosure')}</p>
              </div>
            </div>
            <Progress aria-label={t('assessment.stepOf', { current: step + 1, total: totalSteps })} value={progress} className="h-1.5 mb-8" data-testid="assessment-progress" />
          </>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20" data-testid="assessment-loading">
            <Loader2 className="w-10 h-10 animate-spin text-[#527F19] mb-4" />
            <p className="text-sm text-[#66707A]">{t('assessment.analyzing')}</p>
            <p className="text-xs text-[#8A929B] mt-1">{t('assessment.analyzingTime')}</p>
          </div>
        )}

        {isProfileStep && !loading && !isResultStep && (
          <ProfileStep
            industry={industry}
            setIndustry={setIndustry}
            companySize={companySize}
            setCompanySize={setCompanySize}
            totalSteps={totalSteps}
            canProceed={canProceed}
            onNext={() => setStep(1)}
          />
        )}

        {isQuestionStep && !loading && !isResultStep && (
          <QuestionStep
            step={step}
            totalSteps={totalSteps}
            currentQ={currentQ}
            selections={selections}
            setSelections={setSelections}
            contexts={contexts}
            setContexts={setContexts}
            isLastQuestion={step >= questions.length}
            canProceed={canProceed}
            onBack={() => setStep(step - 1)}
            onNext={() => setStep(step + 1)}
            onSubmit={handleSubmit}
          />
        )}

        {isResultStep && !loading && (
          <ResultsStep
            result={result}
            emailing={emailing}
            onEmail={handleEmail}
            onUseInCalculator={handleUseInCalculator}
          />
        )}
      </div>
    </AppShell>
  );
}
